import React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { styled, TableSortLabel, Typography } from "@mui/material";
import DataTableRow from "./DataTableRow";
import { css } from "@emotion/react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

type Props<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  rowHeight?: number;
  sortById?: string;
  sortDesc?: boolean;
  highlightRow?: boolean;
  highlightRowColor?: string;
  highlightObjectProperty?: string;
  highlightObjectValue?: string;
  noResultFoundText?: string;
  loadingComponent?: React.ReactNode;
  isRowSelectionEnabled?: boolean;
  isMultiRowSelectionEnabled?: boolean;
  isDense?: boolean;
  isLoading?: boolean;
  height?: number;
  tableLayout?: "fixed" | "auto";
  verticalAlign?: "top" | "middle" | "bottom";
  onSortingChange?: OnChangeFn<SortingState>;
};

const DataTable = React.forwardRef<HTMLDivElement, Props<any>>(
  (
    {
      columns,
      data,
      highlightRow = false,
      highlightRowColor = "#fff",
      highlightObjectProperty,
      highlightObjectValue,
      isDense = false,
      isLoading = false,
      isMultiRowSelectionEnabled = false,
      isRowSelectionEnabled = false,
      noResultFoundText = "No result found",
      loadingComponent = "Loading...",
      rowHeight,
      sortById = "id",
      sortDesc = false,
      height,
      tableLayout = "auto",
      verticalAlign = "middle",
      onSortingChange,
      ...otherProps
    },
    ref
  ) => {
    const [sorting, setSorting] = React.useState<SortingState>([{ id: sortById, desc: sortDesc }]);
    const [rowSelection, setRowSelection] = React.useState({});

    const table = useReactTable({
      columns,
      data,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onSortingChange: (state) => {
        if (onSortingChange) {
          onSortingChange(state);
        }
        setSorting(state);
      },
      state: {
        rowSelection,
        sorting,
      },
      enableRowSelection: isRowSelectionEnabled,
      enableMultiRowSelection: isMultiRowSelectionEnabled,
      enableSortingRemoval: false,
      onRowSelectionChange: setRowSelection,
      getRowId: (row) => row["id"],
    });

    return (
      <StyledTableWrapper $height={height} ref={ref} {...otherProps}>
        <StyledTable $tableLayout={tableLayout}>
          <StyledThead>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    const canSort = header.column.getCanSort();

                    return (
                      <StyledTh
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        $width={header.column.columnDef.size !== 150 ? header.column.columnDef.size : undefined}
                        $canSort={canSort}
                        $isDense={isDense}
                      >
                        {canSort ? (
                          <StyledTableSortLabel
                            active={!!isSorted}
                            direction={isSorted || "asc"}
                            IconComponent={ArrowDropDownIcon}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </StyledTableSortLabel>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </StyledTh>
                    );
                  })}
                </tr>
              );
            })}
          </StyledThead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <StyledDataTableRow
                row={row}
                key={row.index}
                rowHeight={rowHeight}
                isDense={isDense}
                $verticalAlign={verticalAlign}
                $isClickable={isRowSelectionEnabled}
                $bgColor={
                  highlightRow && row.original[`${highlightObjectProperty}`] === highlightObjectValue
                    ? highlightRowColor
                    : ""
                }
              />
            ))}
          </tbody>
        </StyledTable>
        {isLoading && loadingComponent}
        {table.getRowModel().rows.length === 0 && !isLoading && (
          <StyledNoResultPanel>
            <Typography variant={"body2"}>{noResultFoundText}</Typography>
          </StyledNoResultPanel>
        )}
      </StyledTableWrapper>
    );
  }
);

const StyledTableWrapper = styled("div", { shouldForwardProp: (prop) => prop !== "$height" })(
  ({ theme, $height }) => css`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${theme.palette.grey[300]};
    ${$height && `max-height: ${$height}px;`}
  `
);

const StyledTableSortLabel = styled(TableSortLabel)(
  ({ theme }) => css`
    display: inline-flex;
  `
);

const StyledTable = styled("table", { shouldForwardProp: (prop) => prop !== "$tableLayout" })(
  ({ theme, $tableLayout }) => css`
    background-color: ${theme.palette.background.paper};
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    position: relative;
    table-layout: ${$tableLayout};

    & tr > td:last-child {
      border-right: none !important;
    }

    & tr > th:last-child {
      border-right: none !important;
    }

    & tr:last-child > td {
      border-bottom: none !important;
    }
  `
);

const StyledThead = styled("thead")(
  ({ theme }) => css`
    position: sticky;
    z-index: 1;
    top: 0;
  `
);

const StyledTh = styled("th", {
  shouldForwardProp: (prop) =>
    prop !== "$width" && prop !== "$canSort" && prop !== "$isDense" && prop !== "$isCenterAligned",
})<{
  $width: number | undefined;
  $canSort: boolean;
  $isDense: boolean;
  $isCenterAligned: boolean;
}>(
  ({ theme, $width, $canSort, $isDense, $isCenterAligned }) => css`
  height: 56px;
  background-color: ${theme.palette.background.paper};
  border-bottom: 1px solid ${theme.palette.grey[500]};
  border-right: 1px solid ${theme.palette.grey[300]};
  padding: 0 ${theme.spacing(4)};
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  color: ${theme.palette.text.secondary};
  box-sizing: border-box;
  white-space: nowrap;

  ${$isDense && `padding: 0 ${theme.spacing(1)};`}

  &:hover .MuiTableSortLabel-root: {
    background: transparent;
  }

  &:hover  svg {
    opacity: 0.5;
  }

  ${$width && `width: ${$width}px;`}
  ${$canSort && `cursor: pointer`}
  ${$isCenterAligned && `text-align: center`}
`
);

const StyledDataTableRow = styled(DataTableRow, {
  shouldForwardProp: (prop) => !["$verticalAlign", "$bgColor", "$isClickable"].includes(prop.toString()),
})<{
  $bgColor: string;
  $isClickable: boolean;
  $verticalAlign?: string;
}>(
  ({ theme, $bgColor, $isClickable, $verticalAlign }) => css`
    background: ${$bgColor};
    ${$isClickable && `cursor: pointer;`}
    ${$verticalAlign &&
    `
      td {
        vertical-align: ${$verticalAlign};
      }
    `}
  `
);

const StyledNoResultPanel = styled("div")(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: ${theme.palette.background.paper};
  `
);

export default DataTable;
